import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import AboutSection from "./components/AboutSection";
import OurJourney from "./components/OurJourney";
import StrategySection from "./components/StrategySection";
import FundTermsSection from "./components/FundTermsSection";
import PortfolioSection from "./components/PortfolioSection";
import LendingSection from "./components/LendingSection";
import TeamSection from "./components/TeamSection";
import FeaturedInsights from "./components/FeaturedInsights";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import { 
  OrganizationSchema, 
  WebsiteSchema, 
  InvestmentFundSchema,
  LocalBusinessSchema,
  InvestmentServiceSchema 
} from "./components/StructuredData";

// Pages
import AboutPage from "./pages/AboutPage";
import WhyHoustonPage from "./pages/WhyHoustonPage";
import TeamPage from "./pages/TeamPage";
import PortfolioPage from "./pages/PortfolioPage";
import StrategyPage from "./pages/StrategyPage";
import FundTermsPage from "./pages/FundTermsPage";
import InvestmentProcessPage from "./pages/InvestmentProcessPage";
import TrackRecordPage from "./pages/TrackRecordPage";
import HighNetWorthPage from "./pages/HighNetWorthPage";
import FamilyOfficesPage from "./pages/FamilyOfficesPage";
import InstitutionalPage from "./pages/InstitutionalPage";
import FAQPage from "./pages/FAQPage";
import LendingPage from "./pages/LendingPage";
import InsightsPage from "./pages/InsightsPage";
import CaseStudiesPage from "./pages/CaseStudiesPage";
import MarketReportsPage from "./pages/MarketReportsPage";
import InvestorResourcesPage from "./pages/InvestorResourcesPage";
import ContactPage from "./pages/ContactPage";
import PressPage from "./pages/PressPage";
import DisclosuresPage from "./pages/DisclosuresPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import JarmeseApartmentsPage from "./pages/case-studies/JarmeseApartmentsPage";
import NorthgreenAtriumPage from "./pages/case-studies/NorthgreenAtriumPage";
import HollyvaleApartmentsPage from "./pages/case-studies/HollyvaleApartmentsPage";
import GriggsShoppingCenterPage from "./pages/case-studies/GriggsShoppingCenterPage";
import AdminPage from "./pages/AdminPage";
import InsightArticlePage from "./pages/InsightArticlePage";
import PortalPage from "./pages/PortalPage";
import ScrollToTop from "./components/ScrollToTop";

const LandingPage = () => {
  return (
    <div className="bg-[#0A1628] min-h-screen">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>ASAP Capital | Private Equity Real Estate Investments in Houston</title>
        <meta name="description" content="ASAP Capital is a Reg D 506(c) private equity fund offering value-add real estate investments in Greater Houston. Target 20-28% IRR for investors. 18+ years of market expertise." />
        <meta name="keywords" content="private equity real estate, Houston real estate investment, investor, value-add investing, multifamily investment, commercial real estate, REI, Texas real estate" />
        <link rel="canonical" href="https://asapcapitalholdings.com" />
        
        {/* Open Graph */}
        <meta property="og:title" content="ASAP Capital | Private Equity Real Estate Investments" />
        <meta property="og:description" content="Value-add real estate investments for investors. 18+ years Houston market expertise. Target 20-28% IRR." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://asapcapitalholdings.com" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_realestate-fund-7/artifacts/hywwykpi_eb79b399-a596-460e-85f0-525cccff1722.png" />
      </Helmet>
      
      {/* Structured Data for SEO */}
      <OrganizationSchema />
      <WebsiteSchema />
      <InvestmentFundSchema />
      <LocalBusinessSchema />
      <InvestmentServiceSchema />
      
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <StrategySection />
        <OurJourney />
        <FundTermsSection />
        <PortfolioSection />
        <LendingSection />
        <TeamSection />
        <FeaturedInsights />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/why-houston" element={<WhyHoustonPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/strategy" element={<StrategyPage />} />
            <Route path="/fund-terms" element={<FundTermsPage />} />
            <Route path="/investment-process" element={<InvestmentProcessPage />} />
            <Route path="/track-record" element={<TrackRecordPage />} />
            <Route path="/investors/high-net-worth" element={<HighNetWorthPage />} />
            <Route path="/investors/family-offices" element={<FamilyOfficesPage />} />
            <Route path="/investors/institutional" element={<InstitutionalPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/lending" element={<LendingPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/market-reports" element={<MarketReportsPage />} />
            <Route path="/resources" element={<InvestorResourcesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/press" element={<PressPage />} />
            <Route path="/disclosures" element={<DisclosuresPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/case-studies/jarmese-apartments" element={<JarmeseApartmentsPage />} />
            <Route path="/case-studies/northgreen-atrium" element={<NorthgreenAtriumPage />} />
            <Route path="/case-studies/hollyvale-apartments" element={<HollyvaleApartmentsPage />} />
            <Route path="/case-studies/griggs-shopping-center" element={<GriggsShoppingCenterPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/insights/:slug" element={<InsightArticlePage />} />
            <Route path="/portal" element={<PortalPage />} />
          </Routes>
          <CookieConsent />
        </BrowserRouter>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#111D35',
              border: '1px solid rgba(212,175,55,0.2)',
              color: '#fff',
            },
          }}
        />
      </div>
    </HelmetProvider>
  );
}

export default App;
